import { useMemo, useState } from 'react';
import { isEmpty } from 'lodash-es';
import type { AxiosError } from 'axios';

import { checkout } from '@hotjar/checkout';
import { useProductCatalog as useProductCatalogV3 } from '@hotjar/product-catalog';
import type { BillingPeriod, SelectedPlans } from '@hotjar/subscription-management';
import {
  mapSelectedPlansToV3ProductAllowances,
  updateDraftOrder,
  useSubscriptionInfo,
} from '@hotjar/subscription-management';
import { PaymentData } from '@hotjar/webapp/billing/plan';
import { useOwnedAccount, isAccountOwner } from '@hotjar/account';
import { useSites, requestPlanUpdate } from '@hotjar/site';
import { useCurrentUser } from '@hotjar/user';
import type { SiteAccount } from '@hotjar/account';
import { isBillingV3Enabled } from '@hotjar/webapp/domains/billing/features';
import { mapSelectedPlansToSitePlanInfos } from '@hotjar/webapp/domains/revenue/upsell/utils';
import {
  useAnnualBillingEligibility,
  mapV2BillingCycleToV3BillingPeriod,
  mapV3BillingPeriodToV2BillingCycle,
} from '@hotjar/upsell';
import type { CheckoutConfig } from '@hotjar/checkout';

interface UseUpsellCheckoutProps {
  account: Pick<SiteAccount, 'adminIds' | 'id' | 'features' | 'billingCycle'> | undefined;
  newBillingPeriod?: BillingPeriod;
}

export interface SubmitCheckoutParams {
  referrer: string;
  selectedPlans: SelectedPlans;
  forceLegacyCheckout?: boolean;
}

export interface SubmitCheckoutResult {
  success: boolean;
  upsellFlowFinished: boolean;
  isUpgradeRequest?: boolean;
}

export interface UseUpsellCheckout {
  isLoading: boolean;
  isSubmitting: boolean;
  submitCheckout: (submitParams: SubmitCheckoutParams) => Promise<SubmitCheckoutResult>;
}

const DEFAULT_BILLING_PERIOD = 'MONTHLY';
const DEFAULT_CURRENCY = 'EUR';

/**
 * Manages the handover from upsell to checkout. Handles differences between Billing v2 and v3 internally,
 * exposing one single interface for both use cases.
 *
 * `account` must be either an `OwnedAccount` or `SiteAccount` object. The account can be taken from any site you're
 * upgrading. This code assumes that the upsell's scope is always one account at a time.
 *
 * `newBillingPeriod` only pass something here if you want to change the account's billing period with this checkout.
 * If omitted, the checkout will default to the account's current billing period setting.
 *
 * The logic assumes that preliminary validation has been done, but also does a few checks on its own, and will
 * throw errors accordingly if an invalid scenario is attempted.
 *
 * If you're building an upsell modal, you'll generally want to call the `submitCheckout` function of this hook to
 * handle the upgrade.
 *
 * Usage:
 *
 * ```
 * const { submitCheckout } = useUpsellCheckout({ account });
 *
 * const onUpsellClick = () => {
 *   submitCheckout({
 *     referrer: 'foo',
 *     selectedPlans: {
 *       14: {
 *         analytics_product: {...},
 *         feedback_product: {...},
 *       }
 *     }
 *   })
 * };
 * ```
 */
export function useUpsellCheckout({
  account,
  newBillingPeriod,
}: UseUpsellCheckoutProps): UseUpsellCheckout {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get user and see if they own this account
  const { data: currentUser, isLoading: isLoadingCurrentUser } = useCurrentUser();
  const isOwnerOfSiteAccount = isAccountOwner(currentUser?.id, account);
  const { data: ownedAccount, isLoading: isLoadingOwnedAccount } = useOwnedAccount();

  // Get billing version and subscription info
  const isBillingV3 = isBillingV3Enabled(account);
  const { data: subscription, isLoading: isLoadingSubscription } = useSubscriptionInfo({
    enabled: isBillingV3 && isOwnerOfSiteAccount,
  });
  const { customer } = subscription || {};

  // Get the account's current billing period and see if they want to change it with this checkout
  let currentBillingPeriod: BillingPeriod | undefined;

  if (account && isBillingV3 && customer) {
    currentBillingPeriod = customer.subscription.billingPeriod;
  } else if (account && !isBillingV3) {
    currentBillingPeriod = mapV2BillingCycleToV3BillingPeriod(account.billingCycle);
  }

  const preferredBillingPeriod = newBillingPeriod ?? currentBillingPeriod ?? DEFAULT_BILLING_PERIOD;
  const billingPeriodChanged = newBillingPeriod && preferredBillingPeriod !== currentBillingPeriod;

  // Get v3 products
  const { data: productCatalogV3, isLoading: isLoadingProductCatalogV3 } = useProductCatalogV3({
    billingPeriod: preferredBillingPeriod,
    currency: customer?.currency ?? DEFAULT_CURRENCY,
    enabled: isBillingV3,
  });

  // Get sites
  const {
    query: { data: sites, isLoading: isLoadingSites },
  } = useSites();

  // Get annual eligibility
  const { isEligible: isEligibleForAnnual, isLoading: isLoadingAnnualEligibility } =
    useAnnualBillingEligibility(account);

  const isLoading =
    isLoadingCurrentUser ||
    isLoadingOwnedAccount ||
    isLoadingProductCatalogV3 ||
    isLoadingSites ||
    isLoadingAnnualEligibility ||
    isLoadingSubscription;

  return useMemo(() => {
    return {
      isLoading,
      isSubmitting,
      submitCheckout: async ({
        referrer,
        selectedPlans,
        forceLegacyCheckout = false,
      }: SubmitCheckoutParams) => {
        if (isEmpty(selectedPlans)) {
          throw new Error('useUpsellCheckout(): selectedPlans is empty.');
        }

        if (isLoading) {
          throw new Error(
            'useUpsellCheckout(): Cannot submit checkout before resources finished loading.',
          );
        }

        if (isSubmitting) {
          throw new Error(
            'useUpsellCheckout(): Cannot re-submit checkout while a checkout is already in progress.',
          );
        }

        if (!account) {
          throw new Error('useUpsellCheckout(): Account not available.');
        }

        if (!sites?.length) {
          throw new Error('useUpsellCheckout(): Could not get sites or got empty array.');
        }

        if (!currentUser) {
          throw new Error('useUpsellCheckout(): Could not get current user.');
        }

        // Handle upgrade requests from non-owners
        if (!isOwnerOfSiteAccount) {
          if (billingPeriodChanged) {
            throw new Error(
              'useUpsellCheckout(): Non-owner selected a billing period change which is unsupported.',
            );
          }

          setIsSubmitting(true);

          const sitePlanInfos = mapSelectedPlansToSitePlanInfos(selectedPlans, sites);

          try {
            await Promise.all(
              // Loop over `sitePlanInfos` and submit one upgrade requests per site
              sitePlanInfos.map(async (sitePlanInfo) => {
                try {
                  const { success, error_message: errorMessage } = await requestPlanUpdate({
                    accountId: account.id,
                    siteId: sitePlanInfo.site_id,
                    planId: sitePlanInfo.plan_id,
                    requestedFrom: referrer,
                  });

                  // Returned 200 OK, but an error message too (shouldn't ever happen, but let's be on the safe side)
                  if (!success && errorMessage) {
                    throw new Error(errorMessage);
                  }

                  // Returned 200 OK, but no success and no error message was given. If so, the backend is broken.
                  if (!success && !errorMessage) {
                    throw new Error('No success but no error message was received either.');
                  }
                } catch (error) {
                  if (error && (error as AxiosError)?.isAxiosError) {
                    const axiosError = error as AxiosError;
                    throw new Error(
                      `useUpsellCheckout(): Failed submitting upgrade request (Axios error). Status code: ${
                        axiosError.response?.status
                      }. Response: ${JSON.stringify(axiosError.response?.data)}`,
                    );
                  }

                  if (error && error instanceof Error) {
                    throw new Error(
                      `useUpsellCheckout(): Failed submitting upgrade request. Error: ${error.message}`,
                    );
                  }
                }
              }),
            );

            setIsSubmitting(false);

            return {
              success: true,
              upsellFlowFinished: true,
              isUpgradeRequest: true,
            };
          } catch (error) {
            setIsSubmitting(false);

            if (error && error instanceof Error) {
              throw error;
            }
          }
        }

        if (!ownedAccount) {
          throw new Error(
            'useUpsellCheckout(): User is owner of the account but ownedAccount is unavailable.',
          );
        }

        if (billingPeriodChanged && preferredBillingPeriod === 'ANNUALLY' && !isEligibleForAnnual) {
          throw new Error(
            'useUpsellCheckout(): User selected annual billing but the account is not eligible for it.',
          );
        }

        if (
          billingPeriodChanged &&
          currentBillingPeriod === 'ANNUALLY' &&
          preferredBillingPeriod === 'MONTHLY'
        ) {
          throw new Error(
            'useUpsellCheckout(): User selected monthly billing but is currently on annual.',
          );
        }

        setIsSubmitting(true);

        // Handle billing v3 👇
        // forceLegacyCheckout has been introduced for Trial Users who try to downgrade we need them
        // to go through the Legacy Billing v2 flow,
        // so that it ends up at the `upsert_subscription` endpoint
        if (isBillingV3 && !forceLegacyCheckout) {
          const payload = mapSelectedPlansToV3ProductAllowances({
            productCatalog: productCatalogV3,
            selectedPlans,
            sites,
          });

          // If we need to set a new billing period, save it into a draft order
          if (billingPeriodChanged) {
            await updateDraftOrder(
              {
                type: 'billingPeriod',
                payload: preferredBillingPeriod,
              },
              { checkoutOnSuccess: false },
            );
          }

          // Save selected v3 product allowances into a draft order and navigate to checkout on success
          await updateDraftOrder(
            {
              type: 'products',
              payload,
            },
            { checkoutOnSuccess: true },
          );

          setIsSubmitting(false);

          return {
            success: true,
            upsellFlowFinished: true,
          };
        }

        // Handle billing v2 👇
        // Map `selectedPlans` into `SitePlanInfo` objects. Each object will contain plan change data for one site.
        const billingV2PlanChanges = mapSelectedPlansToSitePlanInfos(selectedPlans, sites);

        // Payload for v2 checkout
        const paymentData = new PaymentData(billingV2PlanChanges, undefined, referrer);
        const payload: CheckoutConfig = {
          account: ownedAccount,
          paymentData,
        };

        if (billingPeriodChanged) {
          payload.billingCycle = mapV3BillingPeriodToV2BillingCycle(preferredBillingPeriod);
        }

        try {
          // Proceed to v2 checkout
          await checkout(payload);

          setIsSubmitting(false);

          // We should first resolve the promise, update states, let React render the UI for one tick, and then reload
          setTimeout(() => {
            window.location.reload();
          }, 0);

          return {
            success: true,
            upsellFlowFinished: true,
          };
        } catch (error) {
          setIsSubmitting(false);

          /**
           * If you're wondering why we're catching an error and then re-throw it, the reason is that `checkout` can
           * reject it's promise with `undefined`, for example if the user cancels the checkout. These are not errors,
           * and we don't want to propagate them as errors. We only want to propagate actual errors, so we need to
           * check if `error` is an instance of `Error`, and only re-throw to the upper scopes if it is.
           */
          if (error && error instanceof Error) {
            throw error;
          }

          /**
           * Unless we have an `Error` object thrown from `checkout`, we have no reason to believe
           * something has failed. Maybe the user just cancelled the checkout flow.
           */
          return {
            success: true,
            upsellFlowFinished: false,
          };
        }
      },
    };
  }, [
    account,
    billingPeriodChanged,
    currentBillingPeriod,
    currentUser,
    isBillingV3,
    isEligibleForAnnual,
    isLoading,
    isOwnerOfSiteAccount,
    isSubmitting,
    ownedAccount,
    preferredBillingPeriod,
    productCatalogV3,
    sites,
  ]);
}
